import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { BookOpen, GraduationCap, Brain, Building2, Scale, Users, Shield, AlertTriangle } from 'lucide-react';
import EmbeddedModule from './EmbeddedModule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPIC_META = {
  'GRC': { icon: Shield, color: 'bg-blue-50 text-blue-700 border-blue-100' },
  'Compliance': { icon: BookOpen, color: 'bg-emerald-50 text-emerald-700 border-emerald-100' },
  'AML': { icon: AlertTriangle, color: 'bg-rose-50 text-rose-700 border-rose-100' },
  'AI Governance': { icon: Brain, color: 'bg-violet-50 text-violet-700 border-violet-100' },
  'AI Core': { icon: Brain, color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
  'AI Generalist': { icon: GraduationCap, color: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100' },
  'How to do AI': { icon: Brain, color: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
  'Legal': { icon: Scale, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  'HR': { icon: Users, color: 'bg-amber-50 text-amber-700 border-amber-100' },
  'Banking': { icon: Building2, color: 'bg-teal-50 text-teal-700 border-teal-100' },
  'Cybersecurity': { icon: Shield, color: 'bg-orange-50 text-orange-700 border-orange-100' }
};

const LEVEL_COLOR = {
  Foundational: 'bg-gray-100 text-gray-700',
  Intermediate: 'bg-blue-100 text-blue-700',
  Advanced: 'bg-violet-100 text-violet-700'
};

const KnowledgeHub = () => {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [topic, setTopic] = useState('all');
  const [level, setLevel] = useState('all');
  const [q, setQ] = useState('');

  useEffect(() => {
    axios.get(`${API}/knowledge`).then((r) => setArticles(r.data)).catch(() => setArticles([]));
    axios.get(`${API}/knowledge/topics`).then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  const topics = useMemo(() => ['all', ...Object.keys(stats?.topics || {})], [stats]);

  const filtered = useMemo(() => {
    return articles
      .filter((a) => (topic === 'all' ? true : a.topic === topic))
      .filter((a) => (level === 'all' ? true : a.level === level))
      .filter((a) => {
        const s = q.trim().toLowerCase();
        if (!s) return true;
        return [a.title, a.summary, ...(a.tags || [])].join(' ').toLowerCase().includes(s);
      });
  }, [articles, topic, level, q]);

  return (
    <EmbeddedModule
      eyebrow="Learn · Edtech-grade"
      title="Knowledge Hub"
      tagline="How-to playbooks across GRC, Compliance, AML, AI Governance, AI Core, AI Generalist, Legal, HR, Banking and Cybersecurity. Built like an ad-tech engine — you discover the right primer in seconds."
      badgeText={`${stats?.total ?? '—'} articles · ${Object.keys(stats?.topics || {}).length} topics`}
      accentClass="from-cyan-700 via-blue-700 to-indigo-800"
      testId="knowledge-hub-page"
      kpis={[
        { label: 'Articles', value: stats?.total ?? '—' },
        { label: 'Topics', value: stats ? Object.keys(stats.topics).length : '—' },
        { label: 'Levels', value: 3 },
        { label: 'Avg. Read', value: '~10 min' }
      ]}
    >
      {/* Topic tiles */}
      <Card data-testid="knowledge-topics">
        <CardHeader>
          <CardTitle>Browse by Topic</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(stats?.topics || {}).map(([t, count]) => {
              const meta = TOPIC_META[t] || { icon: BookOpen, color: 'bg-gray-50 text-gray-700 border-gray-200' };
              const Icon = meta.icon;
              const active = topic === t;
              return (
                <button
                  key={t}
                  data-testid={`topic-${t.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => setTopic(active ? 'all' : t)}
                  className={`border rounded-lg p-3 text-left transition-all ${meta.color} ${
                    active ? 'ring-2 ring-blue-400 shadow-sm' : 'hover:shadow-sm'
                  }`}
                >
                  <Icon className="w-5 h-5 mb-2" />
                  <p className="text-xs font-semibold leading-tight">{t}</p>
                  <p className="text-[11px] opacity-70 mt-0.5">{count} article{count !== 1 ? 's' : ''}</p>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filter strip */}
      <Card data-testid="knowledge-list">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center space-x-2">
              <BookOpen className="w-5 h-5 text-blue-700" />
              <span>Articles</span>
              <Badge variant="secondary">{filtered.length}</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by title, tag, summary..."
                className="h-9 w-72"
                data-testid="knowledge-search"
              />
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                data-testid="knowledge-level"
                className="h-9 px-3 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All levels</option>
                <option>Foundational</option>
                <option>Intermediate</option>
                <option>Advanced</option>
              </select>
            </div>
          </div>
          {topic !== 'all' && (
            <div className="mt-2">
              <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => setTopic('all')}>
                Topic: {topic} · clear
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((a) => (
              <div
                key={a.id}
                data-testid={`article-${a.slug}`}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-[10px]">{a.topic}</Badge>
                  <Badge className={`text-[10px] ${LEVEL_COLOR[a.level] || ''}`}>{a.level}</Badge>
                </div>
                <p className="text-sm font-semibold text-gray-900 leading-snug">{a.title}</p>
                <p className="text-xs text-gray-600 mt-2 line-clamp-3">{a.summary}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-[11px] text-gray-500">{a.read_minutes} min read</span>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {(a.tags || []).slice(0, 2).map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>
                    ))}
                  </div>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-6 col-span-full">No articles match.</p>
            )}
          </div>
        </CardContent>
      </Card>
    </EmbeddedModule>
  );
};

export default KnowledgeHub;
